import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, Heart, Gift, ChevronLeft, Clock, Star, ArrowRight, Share2, Plus, ShoppingCart, X } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import CartSidebar from '../components/CartSidebar';
import BottomNavigation from '../components/BottomNavigation';
import CartDiscountProgressBar from '../components/CartDiscountProgressBar';
import BranchSelector from '../components/BranchSelector';
import PWAInstallButton from '../components/PWAInstallButton';
import { useTheme } from '../context/ThemeContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import PaymentPricesList from '../components/PaymentPricesList';
import { ComboBannerPreview } from '../components/ComboBannerPreview';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


// ── Featured Product Carousel Card ──
const FeaturedCard = ({ product, theme }) => {
  const { addToCart, cart, updateQuantity, getPriceForQuantity, getRawCartTotal, selectedPaymentMethod, resolveLineId } = useCart();
  const { user, storeSettings, getEffectiveCustomerType, getListedPrice, getBestDiscountPct, getWinnerDiscountPct, isFeatureEnabled } = useAuth();
  const [justAdded, setJustAdded] = useState(false);
  const _lineId = resolveLineId ? resolveLineId(product.id) : product.id;
  const cartItem = cart.find(item => item.id === _lineId);
  const currentQty = cartItem ? cartItem.quantity : 0;
  const packSize = product.packSize || 1;
  const minQuantity = Math.max(1, Number(product.minQuantity) || 1);
  const price = getPriceForQuantity(product, currentQty || minQuantity);
  // 🚦 Feature flags: si productQuantityDiscounts está OFF, ignoramos
  // los tiers por cantidad; si paymentMethodDiscounts está OFF, escondemos
  // la lista de precios por forma de pago.
  const ffQtyDisc = isFeatureEnabled ? isFeatureEnabled('productQuantityDiscounts') : true;
  const ffPmDisc = isFeatureEnabled ? isFeatureEnabled('paymentMethodDiscounts') : true;
  const hasQtyPrices = ffQtyDisc && !!(product.quantityPrices && product.quantityPrices.length > 0);
  const fmt = (n) => `$${(n || 0).toLocaleString('es-AR')}`;
  // Solo COMERCIOS ven los precios por forma de pago. Consumidor final jamás.
  // Tampoco si el producto tiene descuento por cantidad (no se apilan).
  const _eff = getEffectiveCustomerType ? getEffectiveCustomerType() : user?.customer_type;
  const _isConsumerFinal = _eff === 'consumidor_final';
  // Cliente con descuento fijo (permanente) = 6% ÚNICO compre lo que compre:
  // no mostramos ofertas por cantidad (no aplican para él).
  const _hasPermDisc = Number(user?.permanentDiscountPct || 0) > 0;
  // Consumidor final NO ve "Oferta por cantidad" tampoco.
  const showQtyOffer = hasQtyPrices && !_isConsumerFinal && !_hasPermDisc;
  const _showPaymentPrices = _eff === 'comercio' && !hasQtyPrices && ffPmDisc;
  const _pmList = storeSettings?.paymentMethods || [];
  // Precio tachado + badge: mostramos el WINNER (permanente o por objetivo de
  // carrito), NO cuando el descuento gana un tier por cantidad (ese caso ya
  // tiene su propia UI). Producto excluido → nunca tachado por cart-discount.
  const rawTotalNow = typeof getRawCartTotal === 'function' ? getRawCartTotal() : 0;
  const winnerPct = getWinnerDiscountPct
    ? Number(getWinnerDiscountPct(product, rawTotalNow, currentQty || minQuantity, selectedPaymentMethod) || 0)
    : (getBestDiscountPct
        ? Number(getBestDiscountPct(product, rawTotalNow, currentQty || minQuantity, selectedPaymentMethod) || 0)
        : Number(user?.permanentDiscountPct || 0));
  const basePriceRaw = Number(product?.priceNumeric || 0);
  const listedPrice = (winnerPct > 0 && getListedPrice) ? getListedPrice(basePriceRaw, 0) : null;
  const showPermDiscount = winnerPct > 0 && listedPrice && listedPrice > price;

  const handleAdd = () => {
    // 🔢 Step = minQuantity (multiplos del mínimo).
    const stockAvail = (typeof product?.stock === 'number' && product.stock >= 0) ? product.stock : Infinity;
    const firstAdd = (stockAvail > 0 && stockAvail < minQuantity) ? stockAvail : minQuantity;
    const res = (currentQty === 0)
      ? addToCart(product, firstAdd)
      : updateQuantity(_lineId, currentQty + minQuantity);
    if (res && !res.ok) return;
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 1200);
  };
  const handleDecrement = () => {
    if (currentQty <= 0) return;
    const next = currentQty - minQuantity;
    if (next < minQuantity) updateQuantity(_lineId, 0);
    else updateQuantity(_lineId, next);
  };

  return (
    <div className="flex-shrink-0 w-56 sm:w-60 rounded-xl overflow-hidden shadow-md bg-white border border-slate-100 relative" data-testid={`featured-${product.id}`}>
      {showQtyOffer && (
        <div className="absolute top-0 left-0 right-0 z-10 py-2 text-center" style={{ background: 'linear-gradient(135deg, #F59E0B, #D97706)', fontFamily: 'Barlow Condensed' }} data-testid="qty-offer-banner">
          <p className="text-white font-bold text-sm tracking-wide">OFERTA POR CANTIDAD</p>
          <div className="flex justify-center gap-2 mt-1">
            {product.quantityPrices.slice(0, 2).map((qp, i) => (
              <span key={i} className="text-xs bg-white/25 text-white px-2 py-0.5 rounded-full font-bold">{qp.minQty}+ → {fmt(qp.priceNumeric)}</span>
            ))}
          </div>
        </div>
      )}
      <div className={`bg-slate-50 flex items-center justify-center overflow-hidden ${showQtyOffer ? 'h-40 pt-14' : 'h-40'}`}>
        <img src={encodeURI(product.imageUrl || '') + (product.imageUrl ? '?w=300' : '')} alt={product.name} className="w-full h-full object-contain p-2" loading="lazy" />
      </div>
      <div className="p-3">
        <h4 className="text-sm font-semibold text-slate-800 leading-tight line-clamp-2 h-10" translate="no">{product.name}</h4>
        {minQuantity > 1 && (
          <p className="text-[10px] font-semibold text-slate-500 mt-0.5" data-testid={`featured-min-${product.id}`}>
            Mínimo: {minQuantity} un.
          </p>
        )}
        <div className="flex items-end justify-between mt-2">
          <div className="flex flex-col">
            {showPermDiscount && (
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="text-[10px] line-through text-slate-400" data-testid={`featured-listed-price-${product.id}`}>{fmt(listedPrice)}</span>
                <span className="text-[9px] bg-emerald-100 text-emerald-700 font-bold px-1.5 py-0.5 rounded-full" data-testid={`featured-discount-badge-${product.id}`}>-{Math.round(winnerPct)}%</span>
              </div>
            )}
            {!showPermDiscount && _eff === 'comercio' && storeSettings?.showMaxDiscountPreview && getBestDiscountPct && (() => {
              const _previewPct = Number(getBestDiscountPct(product, 999999999999, currentQty || minQuantity, null) || 0);
              if (_previewPct <= 0) return null;
              const _topTier = (storeSettings?.cartDiscountTiers || []).slice().sort((a, b) => Number(b.pct || 0) - Number(a.pct || 0))[0];
              const _previewPrice = Math.ceil(basePriceRaw * (1 - _previewPct / 100));
              return (
                <span className="text-[9px] bg-orange-100 text-orange-700 font-bold px-1.5 py-0.5 rounded-full mb-0.5 w-fit" data-testid={`featured-max-discount-preview-${product.id}`}>
                  🔥 {fmt(_previewPrice)} desde ${Math.round(_topTier?.threshold || 0).toLocaleString('es-AR')}
                </span>
              );
            })()}
            <p className="text-lg font-bold" style={{ color: theme.primary }}>{fmt(price)}</p>
          </div>
          {currentQty > 0 ? (
            <div className="flex items-center gap-1.5">
              <button onClick={handleDecrement} className="w-8 h-8 rounded-md bg-slate-200 flex items-center justify-center text-sm font-bold" data-testid={`featured-decrease-${product.id}`}>−</button>
              <span className="text-sm font-bold w-6 text-center" data-testid={`featured-qty-${product.id}`}>{currentQty}</span>
              <button onClick={handleAdd} className="w-8 h-8 rounded-md text-white flex items-center justify-center" style={{ backgroundColor: theme.primary }} data-testid={`featured-increase-${product.id}`}><Plus size={16} /></button>
            </div>
          ) : (
            <button onClick={handleAdd} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-white text-xs font-bold transition-all ${justAdded ? 'scale-95' : ''}`}
              style={{ backgroundColor: justAdded ? '#10B981' : theme.primary }} data-testid={`featured-add-${product.id}`}>
              {justAdded ? '✓' : <><ShoppingCart size={14} /> Agregar</>}
            </button>
          )}
        </div>
        {/* Precios por forma de pago — solo COMERCIO. Consumidor final no los ve. */}
        {_showPaymentPrices && (
          <PaymentPricesList
            basePrice={price}
            paymentMethods={_pmList}
            allowedRestricted={user?.allowedPaymentMethods}
            variant="tiny"
          />
        )}
      </div>
    </div>
  );
};

// ── Auto-scrolling carousel hook ──
const useAutoScroll = (ref, speed = 1, interval = 30) => {
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);

  useEffect(() => { pausedRef.current = paused; }, [paused]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const tick = setInterval(() => {
      if (pausedRef.current) return;
      el.scrollLeft += speed;
      if (el.scrollLeft >= el.scrollWidth - el.clientWidth - 2) {
        el.scrollLeft = 0;
      }
    }, interval);
    return () => clearInterval(tick);
  }, [ref, speed, interval]);

  return { setPaused };
};

// ── Featured Carousel Container ──
const FeaturedCarousel = ({ products, theme }) => {
  const scrollRef = useRef(null);
  const { setPaused } = useAutoScroll(scrollRef, 1, 30);

  return (
    <div className="mb-5" data-testid="featured-carousel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Star size={18} className="text-amber-500" />
          <h3 className="text-base font-bold text-slate-700 uppercase tracking-wide" style={{ fontFamily: 'Barlow Condensed' }}>Destacados</h3>
        </div>
        <span className="text-xs text-slate-400 font-medium">{products.length} productos</span>
      </div>
      <div
        ref={scrollRef}
        className="overflow-x-auto pb-2 -mx-1 scrollbar-hide"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => { setTimeout(() => setPaused(false), 3000); }}
      >
        <div className="flex gap-3 min-w-max px-1">
          {products.map(p => (
            <FeaturedCard key={p.id} product={p} theme={theme} />
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Combo Banner Overlay (click en el catálogo → ver/descargar, mismo
//    sistema que el banner de producto) ──
const ComboBannerOverlay = ({ combo, onClose }) => {
  const { user } = useAuth();
  const { storeSettings } = useCart();
  const [busy, setBusy] = useState(false);
  const isLocalAdmin = typeof window !== 'undefined' && localStorage.getItem('bebida-bacana-admin') === 'true';
  const canDownload = isLocalAdmin || user?.role === 'admin' || user?.role === 'seller';

  const fetchBannerBlob = async () => {
    const url = `${BACKEND_URL}/api/broadcast-assets/combo-banner/${encodeURIComponent(combo.id)}.png?format=ig-story&t=${Date.now()}`;
    const isRetryable = (status) => status === 502 || status === 503 || status === 504;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const r = await fetch(url, { cache: 'no-cache' });
        if (r.ok) return await r.blob();
        if (!isRetryable(r.status) || attempt === 3) throw new Error(`No se pudo generar el banner. HTTP ${r.status}`);
      } catch (e) {
        if (attempt === 3) throw e;
      }
      await new Promise((resolve) => setTimeout(resolve, 1500 * attempt));
    }
  };

  const share = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setBusy(true);
    const publicUrl = `${BACKEND_URL}/api/broadcast-assets/combo-banner/${encodeURIComponent(combo.id)}.png`;
    try {
      const blob = await fetchBannerBlob();
      const file = new File([blob], `${combo.name.replace(/\s+/g, '_')}.png`, { type: 'image/png' });
      if (typeof navigator !== 'undefined' && navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: combo.name, text: combo.name });
          return;
        } catch (err) {
          if (err && err.name === 'AbortError') return;
        }
      }
      if (typeof navigator !== 'undefined' && navigator.share) {
        try {
          await navigator.share({ title: combo.name, text: `${combo.name}\n${publicUrl}`, url: publicUrl });
          return;
        } catch (err) {
          if (err && err.name === 'AbortError') return;
        }
      }
      try { if (navigator.clipboard && navigator.clipboard.writeText) await navigator.clipboard.writeText(publicUrl); } catch (_) {}
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = file.name;
      document.body.appendChild(a); a.click();
      setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 1500);
      alert('Listo: imagen descargada y link copiado al portapapeles.\nPegá el link en WhatsApp para compartir.');
    } catch (err) {
      alert('Error al generar la imagen: ' + (err && err.message ? err.message : err));
    } finally { setBusy(false); }
  };

  const downloadOnly = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setBusy(true);
    try {
      const blob = await fetchBannerBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${combo.name.replace(/\s+/g, '_')}.png`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 1500);
    } catch (err) {
      alert('Error al descargar: ' + (err && err.message ? err.message : err));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col items-center justify-center p-3 overflow-y-auto" onClick={onClose} data-testid="combo-banner-overlay">
      <button onClick={onClose} className="absolute top-3 right-3 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white" data-testid="combo-banner-close">
        <X size={22} />
      </button>
      <div onClick={(e) => e.stopPropagation()} className="relative rounded-3xl overflow-hidden" style={{ width: 'min(430px, 85vw)' }}>
        <ComboBannerPreview combo={combo} storeSettings={storeSettings} format="ig-story" previewW={540} />
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-center gap-2 max-w-md" onClick={(e) => e.stopPropagation()}>
        {canDownload ? (
          <>
            <button onClick={share} disabled={busy}
              className="px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-full font-bold flex items-center gap-2 text-sm"
              data-testid="combo-banner-share">
              📲 {busy ? 'Generando...' : 'Compartir'}
            </button>
            <button onClick={downloadOnly} disabled={busy}
              className="px-5 py-2.5 bg-white/15 hover:bg-white/25 text-white rounded-full font-bold flex items-center gap-2 text-sm"
              data-testid="combo-banner-download">
              📥 Descargar
            </button>
          </>
        ) : (
          <button onClick={onClose} className="px-5 py-2.5 bg-white/15 hover:bg-white/25 text-white rounded-full font-bold flex items-center gap-2 text-sm">
            Cerrar
          </button>
        )}
      </div>
      <p className="mt-2 text-[11px] text-white/60 text-center px-6">
        {canDownload ? 'Ideal para compartir en WhatsApp, Instagram o Facebook' : 'Vista ampliada del combo'}
      </p>
    </div>
  );
};

// ── Combo Card ──
const ComboCard = ({ combo, onAdd, theme }) => {
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const { getEffectiveCustomerType, storeSettings } = useAuth();
  const fmt = (n) => `$ ${(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 0 })}`;
  // Recargo por consumidor final: se aplica al precio de LISTA de cada producto
  // ANTES de calcular el descuento del combo.
  const _eff = getEffectiveCustomerType ? getEffectiveCustomerType() : null;
  const _surchargePct = (_eff === 'consumidor_final' && storeSettings) ? (storeSettings.retailSurchargePercent || 0) : 0;
  const surchargeFactor = 1 + _surchargePct / 100;
  const discountFactor = combo.originalTotal > 0 ? combo.comboPrice / combo.originalTotal : 1;
  const originalTotalWithSurcharge = (combo.originalTotal || 0) * surchargeFactor;
  const calculatedTotal = combo.products?.reduce((sum, p) => {
    let u;
    if (combo.pricingMode === 'manual') { u = Math.round((p.comboUnitPrice ?? p.originalPrice) * surchargeFactor / 10) * 10; }
    else { u = Math.round((p.originalPrice || 0) * surchargeFactor * discountFactor / 10) * 10; }
    return sum + u * p.quantity;
  }, 0) || 0;
  const savings = originalTotalWithSurcharge - calculatedTotal;
  const hasBonif = combo.products?.some(p => p.bonificado || p.comboUnitPrice === 0);
  return (
    <div className="flex-shrink-0 w-64 sm:w-72 bg-white rounded-xl border-2 border-amber-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow" data-testid={`combo-card-${combo.id}`}>
      <div className="bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2.5 flex items-center justify-between cursor-pointer"
        onClick={() => setShowBanner(true)} data-testid={`open-combo-banner-${combo.id}`} title="Ver banner del combo">
        <div className="flex items-center gap-2">
          <span className="text-white font-bold text-xs uppercase tracking-wide">Combo</span>
          {hasBonif && <span className="bg-white/25 text-white px-2 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1"><Gift size={10} /> Bonif</span>}
        </div>
        {combo.discount > 0 && (
          <span className="bg-white text-orange-600 px-2.5 py-0.5 rounded-full text-sm font-black shadow-lg animate-pulse">-{Math.round(combo.discount)}%</span>
        )}
      </div>
      <div className="p-3">
        <h3 className="font-bold text-sm mb-1" style={{ color: theme.text }}>{combo.name}</h3>
        <div className="space-y-1 mb-2">
          {combo.products?.map((p, i) => {
            const isBonif = p.bonificado || p.comboUnitPrice === 0;
            let unitPrice;
            if (combo.pricingMode === 'manual') { unitPrice = Math.round((p.comboUnitPrice ?? p.originalPrice) * surchargeFactor / 10) * 10; }
            else { unitPrice = Math.round((p.originalPrice || 0) * surchargeFactor * discountFactor / 10) * 10; }
            const originalWithSurcharge = Math.round(p.originalPrice * surchargeFactor);
            return (
              <div key={i} className="flex items-center justify-between gap-1 text-xs">
                <span className="text-slate-600 flex-1 truncate">{p.quantity}x {p.name}</span>
                {isBonif ? <span className="font-bold text-green-600 text-[10px]">BONIF</span> :
                  unitPrice < originalWithSurcharge ? (
                    <><span className="line-through text-slate-300">{fmt(originalWithSurcharge)}</span><span className="font-bold" style={{ color: theme.primary }}>{fmt(unitPrice)}</span></>
                  ) : <span className="font-bold" style={{ color: theme.primary }}>{fmt(originalWithSurcharge)}</span>
                }
              </div>
            );
          })}
        </div>
        {savings > 0 && <div className="mb-2 py-1.5 bg-green-50 border border-green-200 rounded-lg text-center"><span className="text-green-700 font-black text-xs">AHORRA {fmt(savings * qty)}</span></div>}
        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <div>
            <span className="text-xs line-through text-slate-400 block">{fmt(originalTotalWithSurcharge * qty)}</span>
            <span className="font-black text-lg" style={{ color: theme.primary }}>{fmt(calculatedTotal * qty)}</span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-7 h-7 bg-slate-200 rounded-lg flex items-center justify-center font-bold text-sm">−</button>
            <span className="w-5 text-center font-bold text-sm">{qty}</span>
            <button onClick={() => setQty(qty + 1)} className="w-7 h-7 rounded-lg text-white flex items-center justify-center font-bold text-sm" style={{ backgroundColor: theme.primary }}>+</button>
            <button onClick={() => { onAdd(combo, qty); setQty(1); setAdded(true); setTimeout(() => setAdded(false), 1500); }}
              className={`px-3 py-1.5 rounded-lg text-white font-bold text-xs ml-1 transition-all ${added ? 'scale-95' : ''}`}
              style={{ backgroundColor: added ? '#10B981' : theme.primary }}
              data-testid={`add-combo-${combo.id}`}>
              {added ? 'Agregado' : 'Agregar'}
            </button>
          </div>
        </div>
      </div>
      {showBanner && <ComboBannerOverlay combo={combo} onClose={() => setShowBanner(false)} />}
    </div>
  );
};

// ── Main Page ──
const ProductListPage = () => {
  const { theme } = useTheme();
  const { favorites, addComboToCart, setIsCartOpen, isFavorite, addToCart, cart, updateQuantity, resolveLineId, activeBranch, setActiveBranch } = useCart();
  const { user, storeSettings, getEffectiveCustomerType, isFeatureEnabled } = useAuth();
  const [products, setProducts] = useState([]);
  const [combos, setCombos] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [banners, setBanners] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // 🔍 Estado del buscador colapsable (thin pill ↔ input expandido).
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false); // eslint-disable-line no-unused-vars
  const searchInputRef = useRef(null);
  // 🧷 Scroll inteligente del header: al bajar se oculta con translateY(-100%),
  // al subir apenas un poco vuelve a aparecer. Usamos position:fixed en el
  // wrapper + paddingTop dinámico en el main (medido con ResizeObserver) para
  // que la transición sea fluida y NO produzca saltos en el catálogo.
  const [headerVisible, setHeaderVisible] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [discountBarHeight, setDiscountBarHeight] = useState(0);
  const headerWrapperRef = useRef(null);
  const lastScrollYRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [previouslyPurchased, setPreviouslyPurchased] = useState([]);
  const [purchasedNames, setPurchasedNames] = useState(new Set());
  const [referralCode, setReferralCode] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  const grupo = searchParams.get('grupo');
  const filter = searchParams.get('filter');
  const bannerId = searchParams.get('banner');
  const highlightProductId = searchParams.get('producto');

  const isFiltered = grupo || filter || bannerId;

  useEffect(() => {
    // Load previously purchased from localStorage
    const orderHistory = JSON.parse(localStorage.getItem('bebida-bacana-orders') || '[]');
    const freq = {};
    orderHistory.flatMap(o => o.items || []).forEach(id => { freq[id] = (freq[id] || 0) + 1; });
    setPreviouslyPurchased(Object.entries(freq).sort((a, b) => b[1] - a[1]).map(([id]) => id));
    fetchData();
  }, []);

  // Detecta dirección de scroll y muestra/oculta el header sticky. Threshold
  // más generoso en mobile para evitar que el URL bar de Safari/Chrome (que
  // colapsa/expande al scrollear) dispare cambios erráticos de visibilidad
  // que empujen la barra de descuentos fuera del viewport.
  useEffect(() => {
    let raf = null;
    const compute = () => {
      raf = null;
      const y = window.scrollY || window.pageYOffset || 0;
      const prev = lastScrollYRef.current;
      const delta = y - prev;
      // Zona muerta: si el usuario está cerca del tope, mostrar sí o sí.
      if (y < 80) {
        setHeaderVisible(true);
        lastScrollYRef.current = y;
        return;
      }
      // Umbrales más altos → filtra el "jitter" del URL bar móvil.
      if (delta > 12) {
        setHeaderVisible(false);
        lastScrollYRef.current = y;
      } else if (delta < -12) {
        setHeaderVisible(true);
        lastScrollYRef.current = y;
      }
    };
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(compute);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  // Mide la altura real del wrapper (varía con logo, buscador expandido, etc.)
  // y la aplica como paddingTop al main para evitar saltos.
  useEffect(() => {
    const el = headerWrapperRef.current;
    if (!el) return;
    const update = () => setHeaderHeight(el.getBoundingClientRect().height);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [searchExpanded]);

  // Idem para la barra de descuentos (sticky independiente).
  useEffect(() => {
    const el = document.querySelector('[data-testid="discount-bar-sticky"]');
    if (!el) return;
    const update = () => setDiscountBarHeight(el.getBoundingClientRect().height);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);


  // Debounce search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchTerm]);

  useEffect(() => { applyFilters(); }, [products, filter, grupo, bannerId, debouncedSearch, favorites, banners]);

  useEffect(() => {
    if (highlightProductId && !loading && filteredProducts.length > 0) {
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="product-card-${highlightProductId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-2', 'ring-amber-400', 'ring-offset-2');
          setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400', 'ring-offset-2'), 3000);
        }
      }, 300);
    }
  }, [highlightProductId, loading, filteredProducts]);

  // Fetch referral code + check redeemable rewards
  const [redeemableRewards, setRedeemableRewards] = useState([]);
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/user/points`, { withCredentials: true })
      .then(r => {
        if (r.data?.referral_code) setReferralCode(r.data.referral_code);
        if (r.data?.has_redeemable && Array.isArray(r.data.redeemable_rewards)) {
          setRedeemableRewards(r.data.redeemable_rewards);
        }
      })
      .catch(() => {});
    // Fetch purchased product names
    axios.get(`${API}/my-purchased-products`, { withCredentials: true })
      .then(r => { if (Array.isArray(r.data)) setPurchasedNames(new Set(r.data)); })
      .catch(() => {});
  }, [user]);

  const fetchData = async (retry = 0) => {
    setLoadError(false);
    try {
      // Pasar el ctype como hint para el endpoint /combos (soporta guests
      // sin sesión que eligieron "Soy Particular" en la Welcome).
      const _eff = getEffectiveCustomerType ? getEffectiveCustomerType() : (user?.customer_type || null);
      const combosUrl = _eff === 'consumidor_final' ? `${API}/combos?ctype=consumidor_final` : `${API}/combos`;
      // Parallel fetches - each independent so one failure doesn't block others
      const [prodRes, combosRes, notifRes, bannersRes] = await Promise.allSettled([
        axios.get(`${API}/products`),
        axios.get(combosUrl),
        axios.get(`${API}/notifications`),
        axios.get(`${API}/banners`)
      ]);
      if (prodRes.status === 'fulfilled') {
        setProducts(prodRes.value.data);
      } else if (retry < 2) {
        // Retry products fetch if failed
        setTimeout(() => fetchData(retry + 1), 1000);
        return;
      } else {
        setLoadError(true);
      }
      if (combosRes.status === 'fulfilled') setCombos(combosRes.value.data);
      if (notifRes.status === 'fulfilled') setNotifications(notifRes.value.data);
      if (bannersRes.status === 'fulfilled') setBanners(bannersRes.value.data);
    } catch (e) {
      if (retry < 2) { setTimeout(() => fetchData(retry + 1), 1000); return; }
      setLoadError(true);
    }
    setLoading(false);
  };

  const getBannerProducts = useCallback((banner) => {
    if (!banner) return [];
    const active = products.filter(p => p.activo);
    if (banner.productIds?.length > 0) return active.filter(p => banner.productIds.includes(p.id));
    if (banner.filterType === 'destacados') return active.filter(p => p.destacado);
    if (banner.filterType === 'ofertas') return active.filter(p => p.oferta || p.quantityPrices?.length > 0);
    if (banner.filterType === 'grupo' && banner.filterValue) return active.filter(p => p.grupo === banner.filterValue);
    return [];
  }, [products]);

  const handleAddCombo = (combo, qty) => { addComboToCart(combo, qty); };

  const handleShareApp = () => {
    const base = window.location.origin;
    const url = referralCode ? `${base}?ref=${referralCode}` : base;
    const msg = referralCode
      ? `Pedí bebidas en *Bebida Bacana* con envío y los mejores precios.\nUsá mi código *${referralCode}* y ganá puntos.\n${url}`
      : `Pedí bebidas en *Bebida Bacana* con envío y los mejores precios.\n${url}`;
    if (navigator.share) {
      navigator.share({ title: 'Bebida Bacana', text: msg }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
    }
  };

  const applyFilters = () => {
    let filtered = products.filter(p => p.activo);
    if (filter === 'favorites') { filtered = filtered.filter(p => favorites.includes(p.id)); }
    else if (filter === 'purchased') { filtered = filtered.filter(p => previouslyPurchased.includes(p.id)); }
    else if (grupo) { filtered = filtered.filter(p => p.grupo === grupo); }
    else if (bannerId) {
      const banner = banners.find(b => b.id === bannerId);
      if (banner) { const bp = getBannerProducts(banner); filtered = filtered.filter(p => bp.some(bp2 => bp2.id === p.id)); }
    }
    if (debouncedSearch) {
      // El buscador del catálogo matchea en: nombre, grupo (categoría),
      // descripción e ID. Permite búsquedas como "vinos", "vodka", "rta1234".
      // Soporta múltiples palabras (todas deben matchear en algún campo).
      const tokens = debouncedSearch
        .toLowerCase()
        .split(/\s+/)
        .filter((t) => t.length > 0);
      filtered = filtered.filter((p) => {
        const haystack = [
          p.name,
          p.grupo,
          p.description,
          p.id,
        ].filter(Boolean).join(' ').toLowerCase();
        return tokens.every((t) => haystack.includes(t));
      });
    }
    filtered.sort((a, b) => a.displayOrder - b.displayOrder);
    setFilteredProducts(filtered);
  };

  // Featured products: use manual selection from admin, fallback to auto
  const featuredProducts = useMemo(() => {
    let arr;
    const manualIds = storeSettings?.featuredProductIds || [];
    if (manualIds.length > 0) {
      arr = manualIds
        .map(id => products.find(p => p.id === id && p.activo))
        .filter(Boolean);
    } else {
      arr = products
        .filter(p => p.activo && (p.tags?.length > 0 || (p.quantityPrices && p.quantityPrices.length > 0)));
    }
    // Shuffle so each visit shows them in different order
    const shuffled = [...arr];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled.slice(0, 30);
  }, [products, storeSettings]);

  // Products user hasn't bought yet (exclude VINOS ALTA GAMA, shuffle)
  const notYetPurchased = useMemo(() => {
    if (!user || purchasedNames.size === 0) return [];
    const eligible = products.filter(p =>
      p.activo && (p.grupo || '').toUpperCase() !== 'VINOS ALTA GAMA' && !purchasedNames.has(p.name)
    );
    const shuffled = [...eligible];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled.slice(0, 20);
  }, [products, purchasedNames, user]);

  // Weekly highlight product
  const weeklyHighlight = useMemo(() => {
    const hid = storeSettings?.weeklyHighlightId;
    if (!hid) return null;
    return products.find(p => p.id === hid && p.activo) || null;
  }, [products, storeSettings]);




  const getGrupoEmoji = (g) => {
    const map = { 'CERVEZAS': '🍺', 'VINOS': '🍷', 'VINOS ALTA GAMA': '🍾', 'GASEOSAS': '🥤', 'ENERGIZANTES': '⚡', 'AGUAS': '💧', 'LICORES': '🥃', 'VODKAS': '🍸', 'WHISKIES': '🥃', 'APERITIVOS': '🍹', 'ESPUMANTES': '🥂', 'SIDRAS': '🍎', 'JUGOS': '🧃', 'RONES': '🍹', 'TEQUILAS': '🌵', 'GINS': '🍸', 'FERNETS': '🌿' };
    return map[g] || '📦';
  };
  const getGrupoColor = (g) => {
    const map = { 'CERVEZAS': '#F59E0B', 'VINOS': '#7C3AED', 'VINOS ALTA GAMA': '#9333EA', 'GASEOSAS': '#EF4444', 'ENERGIZANTES': '#10B981', 'AGUAS': '#0EA5E9', 'LICORES': '#B45309', 'VODKAS': '#6366F1', 'WHISKIES': '#92400E', 'APERITIVOS': '#EC4899', 'ESPUMANTES': '#F472B6', 'SIDRAS': '#84CC16', 'JUGOS': '#F97316', 'RONES': '#B91C1C', 'TEQUILAS': '#65A30D', 'GINS': '#0891B2', 'FERNETS': '#059669' };
    return map[g] || '#6B7280';
  };

  const categories = useMemo(() => {
    const cats = new Set();
    products.filter(p => p.activo).forEach(p => { if (p.grupo) cats.add(p.grupo); });
    return Array.from(cats).sort();
  }, [products]);

  const favoriteProducts = useMemo(() => products.filter(p => p.activo && favorites.includes(p.id)), [products, favorites]);
  const purchasedProducts = useMemo(() => {
    return previouslyPurchased.map(id => products.find(p => p.id === id && p.activo)).filter(Boolean).slice(0, 12);
  }, [products, previouslyPurchased]);

  const getTitle = () => {
    if (filter === 'favorites') return 'MIS FAVORITOS';
    if (filter === 'purchased') return 'COMPRADOS ANTES';
    if (bannerId) { const b = banners.find(bn => bn.id === bannerId); return b?.title || 'PRODUCTOS'; }
    if (grupo) return `${getGrupoEmoji(grupo)} ${grupo}`;
    return null;
  };

  const groupedProducts = () => {
    if (isFiltered) return null;
    const groups = {};
    filteredProducts.forEach(p => {
      const g = p.grupo || 'SIN CATEGORÍA';
      if (!groups[g]) groups[g] = [];
      groups[g].push(p);
    });
    return groups;
  };

  const renderGroupedView = () => {
    const groups = groupedProducts();
    if (!groups) return null;
    const order = ['DESTACADOS', 'OFERTAS', 'CERVEZAS', 'VINOS', 'GASEOSAS', 'ENERGIZANTES', 'AGUAS', 'LICORES'];
    const sorted = Object.keys(groups).sort((a, b) => {
      const ia = order.indexOf(a), ib = order.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1; if (ib === -1) return -1;
      return ia - ib;
    });
    return sorted.map(g => (
      <section key={g} className="mb-6" data-testid={`group-${g}`}>
        <h2 className="text-xl font-bold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
          <span>{getGrupoEmoji(g)}</span>{g}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {groups[g].map(product => (
            <div key={product.id} className={product.bannerTags?.length > 0 ? 'col-span-2 md:col-span-1' : ''}>
              <ProductCard product={product} showBadges={true} isLarge={product.bannerTags?.length > 0} />
            </div>
          ))}
        </div>
      </section>
    ));
  };

  return (
    <div className="min-h-screen pb-16 md:pb-0" style={{ backgroundColor: theme.background }}>
      {/* 🎯 Barra de descuentos SIEMPRE VISIBLE — fixed en top:0 con el
          z-index más alto de la página. Es el PRIMER elemento fijo del
          catálogo: nunca se mueve, nunca se transiciona, nunca se oculta con
          scroll. Vive por ENCIMA del header (z-60 > z-40), así incluso
          durante la animación de ocultar/mostrar el header sigue arriba y
          visible en Safari/Chrome mobile (donde el URL bar oscila y podía
          producir el "aparece y desaparece" reportado).
          Nota: el padding vive DENTRO del CartDiscountProgressBar. El
          wrapper sólo se muestra si hay contenido — así evitamos que un
          `null` de la barra deje una franja vacía que hace fluctuar
          `discountBarHeight` (causa histórica del jitter). */}
      <div
        className="fixed left-0 right-0 z-[60] bg-transparent empty:hidden"
        style={{ top: 0, WebkitTransform: 'translateZ(0)', transform: 'translateZ(0)', willChange: 'transform' }}
        data-testid="discount-bar-sticky"
      >
        <CartDiscountProgressBar />
      </div>

      {/* 🧷 Header + buscador — sits BELOW the discount bar (top: discountBarHeight)
          y se OCULTA con scroll hacia abajo mediante translateY. Nunca cruza
          por encima de la barra porque su z-index es MENOR. */}
      <div
        ref={headerWrapperRef}
        className={`fixed left-0 right-0 z-40 bg-white/95 backdrop-blur-lg border-b border-slate-100 transition-transform duration-300 ease-out ${headerVisible ? 'translate-y-0' : '-translate-y-[200%]'}`}
        style={{ top: `${discountBarHeight}px` }}
        data-testid="sticky-header-wrapper"
      >
        <header className="max-w-7xl mx-auto px-4 py-1">
          {isFiltered ? (
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/products')} className="p-2 hover:bg-slate-50 rounded-lg" data-testid="back-to-catalog">
                <ChevronLeft size={20} className="text-slate-600" />
              </button>
              <h1 className="text-lg font-bold truncate text-slate-800 flex-1" style={{ fontFamily: 'Barlow Condensed' }} data-testid="product-list-title">{getTitle()}</h1>
              <BranchSelector compact />
            </div>
          ) : (() => {
            const align = storeSettings?.logoAlign || 'center';
            // Compactamos el logo: usamos min(logoSize, 40px) para que el header
            // sea más delgado. Respeta la config del admin como máximo, pero
            // en catálogo nunca pasa de 40px de alto.
            const rawLogoSize = Number(storeSettings?.logoSize || 56);
            const logoH = Math.min(rawLogoSize, 40);
            const logoBlock = (
              <div className="flex items-center gap-1 flex-shrink-0 min-w-0">
                <img src="/logo.png" alt={storeSettings?.storeName || "Bebida Bacana"} style={{ height: `${logoH}px` }} className="w-auto max-w-[60vw] object-contain" data-testid="site-logo" />
                {(storeSettings?.titleFontSize || 0) > 0 && (
                  <span style={{ fontSize: `${Math.min(storeSettings.titleFontSize, 16)}px`, color: storeSettings.titleColor || '#1B1F3B', fontFamily: 'Barlow Condensed', fontWeight: 700 }} data-testid="site-title-text">
                    {storeSettings.storeName || 'Bebida Bacana'}
                  </span>
                )}
              </div>
            );
            if (align === 'left') {
              return (
                <div className="flex items-center gap-3" data-testid="header-align-left">
                  {logoBlock}
                  <div className="flex-1" />
                  <BranchSelector compact />
                </div>
              );
            }
            if (align === 'right') {
              return (
                <div className="flex items-center gap-3" data-testid="header-align-right">
                  <BranchSelector compact />
                  <div className="flex-1" />
                  {logoBlock}
                </div>
              );
            }
            return (
              <div
                className="grid items-center gap-2 min-w-0"
                style={{ gridTemplateColumns: 'minmax(0,1fr) auto minmax(0,1fr)' }}
                data-testid="header-align-center"
              >
                <div className="min-w-0" />
                <div className="min-w-0 flex justify-center">
                  <div className="flex items-center gap-1 flex-shrink min-w-0 max-w-[55vw] sm:max-w-[60vw]">
                    <img src="/logo.png" alt={storeSettings?.storeName || "Bebida Bacana"} style={{ height: `${logoH}px` }} className="w-auto max-w-full object-contain" data-testid="site-logo" />
                    {(storeSettings?.titleFontSize || 0) > 0 && (
                      <span style={{ fontSize: `${Math.min(storeSettings.titleFontSize, 16)}px`, color: storeSettings.titleColor || '#1B1F3B', fontFamily: 'Barlow Condensed', fontWeight: 700 }} className="truncate" data-testid="site-title-text">
                        {storeSettings.storeName || 'Bebida Bacana'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="min-w-0 flex justify-end">
                  <BranchSelector compact />
                </div>
              </div>
            );
          })()}
        </header>

        {/* Buscador compacto: pill h-7 (28px) por defecto */}
        <div className="px-3 sm:px-4 py-0.5 max-w-7xl mx-auto">
          <div className="relative">
            {(!searchExpanded) && (
              <button
                type="button"
                onClick={() => {
                  setSearchExpanded(true);
                  setTimeout(() => searchInputRef.current?.focus(), 40);
                }}
                className="w-full h-7 flex items-center gap-2 bg-white border border-orange-300 rounded-full px-3 shadow-sm hover:border-orange-400 transition-all group"
                data-testid="product-search-collapsed"
                aria-label="Buscar productos"
              >
                <Search size={14} className="text-orange-500 flex-shrink-0 group-hover:scale-110 transition-transform" />
                <span className="text-xs text-slate-500 font-medium truncate">Buscar producto…</span>
              </button>
            )}
            {searchExpanded && (
              <div className="relative animate-[searchFade_180ms_ease-out]">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-orange-500" size={18} />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="¿Qué estás buscando?"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => {
                    setSearchFocused(false);
                    setTimeout(() => {
                      if (!(searchInputRef.current && searchInputRef.current.value)) {
                        setSearchExpanded(false);
                      }
                    }, 120);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setSearchTerm('');
                      setSearchExpanded(false);
                      e.currentTarget.blur();
                    }
                  }}
                  className="w-full pl-11 pr-11 py-2 rounded-full text-sm sm:text-base font-semibold border border-orange-300 bg-white shadow focus:border-orange-500 focus:ring-2 focus:ring-orange-100 outline-none transition-all placeholder:text-slate-400 placeholder:font-normal"
                  data-testid="product-search-input"
                />
                {searchTerm && (
                  <button onClick={() => { setSearchTerm(''); searchInputRef.current?.focus(); }} className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-orange-100 hover:bg-orange-200 flex items-center justify-center text-orange-500 transition-colors" data-testid="product-search-clear">
                    <X size={14} />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="px-3 sm:px-4 pb-1 max-w-7xl mx-auto">
          {/* La barra de descuentos vive ARRIBA de este wrapper (fixed en
              top:0, z-[60]). Ver el bloque <div data-testid=discount-bar-sticky>
              al comienzo del return. */}
        </div>
      </div>

      <main
        className="max-w-7xl mx-auto px-3 sm:px-4 pb-4"
        style={{ paddingTop: `${headerHeight + discountBarHeight}px` }}
        data-testid="product-list-main"
      >
        {loading ? (
          <div className="text-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-slate-500 mx-auto"></div></div>
        ) : (
          <>
            {/* ── Only show full homepage content when NOT filtered ── */}
            {!isFiltered && !debouncedSearch && (
              <>
                {/* Banner: tenés puntos disponibles para canjear */}
                {user && redeemableRewards.length > 0 && (
                  <div className="mb-4 p-3 rounded-xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50 flex items-center gap-3" data-testid="redeemable-banner">
                    <span className="text-2xl flex-shrink-0">🎁</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-amber-900">¡Tenés puntos para canjear!</p>
                      <p className="text-xs text-amber-700 mt-0.5">
                        Podés canjear: {redeemableRewards.slice(0, 3).map(r => r.name).join(' · ')}
                        {redeemableRewards.length > 3 && ` y ${redeemableRewards.length - 3} más`}
                      </p>
                    </div>
                    <button onClick={() => navigate('/cuenta')}
                      className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-lg flex-shrink-0"
                      data-testid="btn-go-redeem">
                      Canjear
                    </button>
                  </div>
                )}

                {/* Notifications */}
                {notifications.length > 0 && (
                  <div className="mb-4 space-y-2" data-testid="notifications-banner">
                    {notifications.map(notif => (
                      <div key={notif.id} className={`p-3 rounded-xl border flex items-start gap-3 ${notif.type === 'important' ? 'bg-red-50 border-red-200' : notif.type === 'promo' ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
                        <span className="text-lg flex-shrink-0 mt-0.5">{notif.type === 'important' ? '⚠️' : notif.type === 'promo' ? '🔥' : 'ℹ️'}</span>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-bold ${notif.type === 'important' ? 'text-red-800' : notif.type === 'promo' ? 'text-amber-800' : 'text-blue-800'}`}>{notif.title}</p>
                          <p className={`text-xs mt-0.5 ${notif.type === 'important' ? 'text-red-600' : notif.type === 'promo' ? 'text-amber-600' : 'text-blue-600'}`}>{notif.message}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* ── FEATURED PRODUCTS CAROUSEL ── */}
                {featuredProducts.length > 0 && <FeaturedCarousel products={featuredProducts} theme={theme} />}

                {/* ── COMPRADOS ANTES - Most prominent ── */}
                {purchasedProducts.length > 0 && (
                  <div className="mb-5" data-testid="purchased-before-section">
                    <button onClick={() => navigate('/products?filter=purchased')}
                      className="w-full rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow active:scale-[0.99]"
                      style={{ background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryLight || theme.primary}dd 100%)` }}>
                      <div className="px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Clock size={20} className="text-white" />
                          <span className="text-white font-bold text-base" style={{ fontFamily: 'Barlow Condensed' }}>COMPRADOS ANTES</span>
                        </div>
                        <div className="flex items-center gap-1 text-white/80 text-xs">
                          <span>{purchasedProducts.length} productos</span>
                          <ArrowRight size={14} />
                        </div>
                      </div>
                    </button>
                  </div>
                )}

                {/* ── FAVORITOS - Very prominent ── */}
                {favoriteProducts.length > 0 && (
                  <div className="mb-5" data-testid="favorites-section">
                    <button onClick={() => navigate('/products?filter=favorites')}
                      className="w-full rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow active:scale-[0.99] bg-gradient-to-r from-pink-500 to-rose-500">
                      <div className="px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Heart size={20} className="text-white fill-white" />
                          <span className="text-white font-bold text-base" style={{ fontFamily: 'Barlow Condensed' }}>MIS FAVORITOS</span>
                        </div>
                        <div className="flex items-center gap-1 text-white/80 text-xs">
                          <span>{favoriteProducts.length} productos</span>
                          <ArrowRight size={14} />
                        </div>
                      </div>
                    </button>
                  </div>
                )}

                {/* ── PRODUCTO DESTACADO DE LA SEMANA ── */}
                {weeklyHighlight && !grupo && !filter && !bannerId && (
                  <section className="mb-6" data-testid="weekly-highlight-section">
                    <div className="relative rounded-2xl overflow-hidden shadow-lg border-2" style={{ borderColor: theme.primary }}>
                      <div className="absolute top-0 left-0 right-0 py-1.5 px-4 text-center text-white text-xs font-bold tracking-wider" style={{ backgroundColor: theme.primary, fontFamily: 'Barlow Condensed' }}>
                        PRODUCTO DESTACADO DE LA SEMANA
                      </div>
                      <div className="flex flex-col sm:flex-row items-center gap-4 p-5 pt-10 bg-gradient-to-br from-white to-slate-50">
                        <div className="w-36 h-36 sm:w-44 sm:h-44 flex-shrink-0 rounded-xl overflow-hidden bg-white border border-slate-100 shadow-sm">
                          <img
                            src={encodeURI(weeklyHighlight.imageUrl || '/placeholder.png') + (weeklyHighlight.imageUrl ? '?w=300' : '')}
                            alt={weeklyHighlight.name}
                            className="w-full h-full object-contain p-2"
                          />
                        </div>
                        <div className="flex-1 text-center sm:text-left">
                          <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Barlow Condensed' }}>{weeklyHighlight.name}</h3>
                          {weeklyHighlight.grupo && <p className="text-xs text-slate-400 mb-2">{weeklyHighlight.grupo}</p>}
                          {(() => {
                            // Aplicar recargo consumidor final al precio de lista.
                            const _eff = getEffectiveCustomerType ? getEffectiveCustomerType() : user?.customer_type;
                            const _isConsumer = _eff === 'consumidor_final';
                            const _surchargePct = _isConsumer ? (storeSettings?.retailSurchargePercent || 0) : 0;
                            const _price = Math.ceil((weeklyHighlight.priceNumeric || 0) * (1 + _surchargePct / 100));
                            return (
                              <p className="text-2xl font-black mb-3" style={{ color: theme.primary }}>
                                ${_price.toLocaleString('es-AR')}
                              </p>
                            );
                          })()}
                          {/* Descuento por cantidad — solo para COMERCIO */}
                          {weeklyHighlight.quantityPrices?.length > 0 && getEffectiveCustomerType?.() !== 'consumidor_final' && (
                            <div className="flex flex-wrap gap-1 justify-center sm:justify-start">
                              {weeklyHighlight.quantityPrices.map((qp, i) => (
                                <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                                  {qp.minQty}+ u. → ${(qp.priceNumeric || 0).toLocaleString('es-AR')}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Precios por forma de pago — solo COMERCIO. Consumidor final no los ve.
                              Tampoco si el producto tiene descuento por cantidad (no se apilan). */}
                          {(() => {
                            const _eff = getEffectiveCustomerType ? getEffectiveCustomerType() : user?.customer_type;
                            const _hasQty = !!(weeklyHighlight.quantityPrices && weeklyHighlight.quantityPrices.length > 0);
                            if (_eff !== 'comercio' || _hasQty) return null;
                            const _pmList = storeSettings?.paymentMethods || [];
                            return (
                              <div className="mt-2 text-left">
                                <PaymentPricesList
                                  basePrice={weeklyHighlight.priceNumeric || 0}
                                  paymentMethods={_pmList}
                                  allowedRestricted={user?.allowedPaymentMethods}
                                  variant="compact"
                                />
                              </div>
                            );
                          })()}
                          {(() => {
                            const whLineId = resolveLineId ? resolveLineId(weeklyHighlight.id) : weeklyHighlight.id;
                            const whCartItem = cart.find(it => it.id === whLineId);
                            const whQty = whCartItem ? whCartItem.quantity : 0;
                            // Paso = pedido mínimo (minQuantity). packSize es solo inventario.
                            const whPack = Math.max(1, Number(weeklyHighlight.minQuantity) || 1);
                            if (whQty > 0) {
                              return (
                                <div className="mt-3 inline-flex items-center gap-2 bg-white border-2 rounded-xl p-1 shadow-sm" style={{ borderColor: theme.primary }} data-testid="weekly-highlight-qty-controls">
                                  <button
                                    onClick={() => updateQuantity(whLineId, Math.max(0, whQty - whPack))}
                                    className="w-10 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-black text-lg active:scale-95"
                                    data-testid="weekly-highlight-minus-btn">
                                    −
                                  </button>
                                  <span className="min-w-[40px] text-center font-black text-xl tabular-nums" style={{ color: theme.primary }}>
                                    {whQty}
                                  </span>
                                  <button
                                    onClick={() => updateQuantity(whLineId, whQty + whPack)}
                                    className="w-10 h-10 rounded-lg text-white font-black text-lg active:scale-95"
                                    style={{ backgroundColor: theme.primary }}
                                    data-testid="weekly-highlight-plus-btn">
                                    +
                                  </button>
                                </div>
                              );
                            }
                            return (
                              <button
                                onClick={() => addToCart(weeklyHighlight, whPack)}
                                className="mt-3 px-6 py-2.5 rounded-xl text-white font-bold text-sm shadow-md hover:shadow-lg transition-all active:scale-95"
                                style={{ backgroundColor: theme.primary }}
                                data-testid="weekly-highlight-add-btn"
                              >
                                + Agregar
                              </button>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </section>
                )}

                {/* ── BANNERS custom del admin (categorías/promos). Feature flag
                    `customBanners`: si está OFF, se ocultan del catálogo pero
                    el admin sigue viéndolos en su panel. Los sliders de
                    "Novedades / Lo más vendido / Ofertas" NO son banners
                    custom y siempre se mantienen. ── */}
                {banners.length > 0 && (isFeatureEnabled ? isFeatureEnabled('customBanners') : true) && (
                  <div className="mb-5 overflow-x-auto pb-1" data-testid="banners-section">
                    <div className="flex gap-3 min-w-max">
                      {banners.map(banner => {
                        const count = getBannerProducts(banner).length;
                        const textColor = banner.textColor?.startsWith('#') || banner.textColor?.startsWith('rgb') ? banner.textColor : banner.textColor === 'black' ? '#000000' : '#ffffff';
                        return (
                          <button key={banner.id} onClick={() => navigate(`/products?banner=${banner.id}`)}
                            className="relative rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all active:scale-[0.97] min-w-[200px] max-w-[260px] flex-shrink-0"
                            data-testid={`banner-${banner.id}`}>
                            <div className="h-24" style={{
                              background: banner.bgImage ? `url(${banner.bgImage}) center/cover` :
                                banner.bgColor ? (banner.bgColor.startsWith('#') ? banner.bgColor : undefined) :
                                `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryLight || theme.primary}dd 100%)`
                            }}>
                              {banner.bgImage && <div className="absolute inset-0 bg-black/30" />}
                            </div>
                            <div className="absolute inset-0 flex flex-col justify-center p-4" style={{ color: textColor }}>
                              {banner.emoji && <span className="text-lg mb-0.5">{banner.emoji}</span>}
                              <h3 className="font-bold text-sm leading-tight" style={{ fontFamily: 'Barlow Condensed' }}>{banner.title}</h3>
                              {count > 0 && <span className="text-[10px] opacity-70 mt-0.5">{count} productos</span>}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── COMBOS ── */}
                {combos.length > 0 && (
                  <section className="mb-6" data-testid="combos-section">
                    <h2 className="text-xl font-bold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>COMBOS</h2>
                    <div className="overflow-x-auto pb-2 -mx-1 scrollbar-hide">
                      <div className="flex gap-3 min-w-max px-1">
                        {combos.map(combo => <ComboCard key={combo.id} combo={combo} onAdd={handleAddCombo} theme={theme} />)}
                      </div>
                    </div>
                  </section>
                )}

                {/* ── LO QUE AÚN NO COMPRASTE ── */}
                {notYetPurchased.length > 0 && !grupo && !filter && !bannerId && (
                  <section className="mb-6" data-testid="not-yet-purchased-section">
                    <h2 className="text-lg font-bold mb-3 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed', color: theme.text }}>
                      PRODUCTOS QUE AÚN NO PROBASTE
                    </h2>
                    <div className="overflow-x-auto pb-2 -mx-1 scrollbar-hide">
                      <div className="flex gap-3 min-w-max px-1">
                        {notYetPurchased.map(p => (
                          <FeaturedCard key={p.id} product={p} theme={theme} />
                        ))}
                      </div>
                    </div>
                  </section>
                )}

                {/* ── CATEGORY BANNERS - Normal strip ── */}
                {categories.length > 0 && (
                  <div className="mb-5 overflow-x-auto pb-1" data-testid="category-banners">
                    <div className="flex gap-2 min-w-max">
                      {categories.map(cat => (
                        <button key={cat} onClick={() => navigate(`/products?grupo=${encodeURIComponent(cat)}`)}
                          className="px-4 py-3 rounded-xl text-white font-bold transition-transform hover:scale-105 active:scale-95 shadow-sm flex-shrink-0"
                          style={{ backgroundColor: getGrupoColor(cat) }}>
                          <span className="text-xl block mb-0.5">{getGrupoEmoji(cat)}</span>
                          <span className="text-[10px] block" style={{ fontFamily: 'Barlow Condensed' }}>{cat}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── SHARE APP REFERRAL BANNER ── */}
                {(user || referralCode) && (
                  <button onClick={handleShareApp}
                    className="mb-5 w-full rounded-xl overflow-hidden shadow-md hover:shadow-lg active:scale-[0.98] transition-all"
                    style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%)' }}
                    data-testid="share-app-catalog">
                    <div className="px-4 py-3.5 flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0" style={{ animation: 'pulse 2s ease-in-out infinite' }}>
                        <Share2 size={20} className="text-white" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="text-white font-bold text-sm" style={{ fontFamily: 'Barlow Condensed' }}>INVITÁ AMIGOS Y GANÁ PUNTOS</p>
                        <p className="text-white/80 text-xs">Comparti la app y acumulá puntos canjeables</p>
                      </div>
                      <ArrowRight size={18} className="text-white/70 flex-shrink-0" />
                    </div>
                  </button>
                )}
              </>
            )}

            {/* ── PRODUCTS ── */}
            {filteredProducts.length === 0 && !loading ? (
              <div className="text-center py-12" data-testid="no-products-message">
                <p className="text-slate-500 mb-4">{loadError ? 'Error al cargar productos' : 'No se encontraron productos'}</p>
                <button onClick={() => { setLoading(true); fetchData(); }} className="px-5 py-2.5 text-white rounded-xl font-medium text-sm" style={{ backgroundColor: theme.primary }} data-testid="retry-products-btn">
                  Reintentar
                </button>
              </div>
            ) : (
              <>
                {(isFiltered || debouncedSearch) && (
                  <p className="text-slate-500 text-sm mb-4">{filteredProducts.length} producto{filteredProducts.length !== 1 ? 's' : ''}</p>
                )}
                {!isFiltered && !debouncedSearch ? renderGroupedView() : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {filteredProducts.map(product => (
                      <div key={product.id} className={product.bannerTags?.length > 0 ? 'col-span-2 md:col-span-1' : ''}>
                        <ProductCard product={product} showBadges={true} isLarge={product.bannerTags?.length > 0} />
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>

      <CartSidebar />
      <PWAInstallButton />
      <BottomNavigation />
    </div>
  );
};

export default ProductListPage;
